import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useRouter} from 'next/router';
import {useTranslation} from 'react-i18next';

import {useAuth} from 'UI/hooks/useAuth';
import {useDialogSlot} from 'UI/hooks/useDialogManager';

const DIALOG_PRIORITY = 0;

type UseLanguagePopupReturn = Pick<ReturnType<typeof useTranslation>, 't'> & {
    handleConfirm(): void;
    isOpen: boolean;
};

const STORAGE_KEY = 'language-chosen';

/**
 * Checks whether the user has already chosen a language via localStorage.
 *
 * @returns `true` when a stored preference exists.
 */
const hasStoredChoice = (): boolean => {
    if (typeof window === 'undefined') return true;

    return Boolean(localStorage.getItem(STORAGE_KEY));
};

/**
 * Custom hook for the `LanguagePopup` component.
 * Manages visibility state, browser language detection, and locale switching.
 * Checks the authenticated user's saved locale preference first — if present,
 * the popup is suppressed and the saved locale is applied automatically.
 *
 * Coordinates with the shared `DialogManagerProvider` (see `useDialogManager`) so it doesn't
 * overlap with other global dialogs/banners, such as the PWA install prompt.
 *
 * @returns An object containing:
 * - `handleConfirm`: Callback to dismiss the popup and persist the choice.
 * - `isOpen`: Whether the popup is currently visible.
 * - `t`: Translation function from react-i18next.
 */
export const useLanguagePopup = (): UseLanguagePopupReturn => {
    const router = useRouter();
    const {t} = useTranslation();
    const {isLoading, user} = useAuth();
    const [dismissed, setDismissed] = useState(hasStoredChoice);
    const userLocaleApplied = useRef(false);

    const hasUserLocale = !isLoading && Boolean(user?.locale);
    const wantsOpen = useMemo(() => !isLoading && !dismissed && !hasUserLocale, [isLoading, dismissed, hasUserLocale]);
    const isActive = useDialogSlot('languagePopup', DIALOG_PRIORITY, wantsOpen);
    const isOpen = wantsOpen && isActive;

    useEffect(() => {
        if (isLoading || userLocaleApplied.current) return;
        if (!user?.locale) return;

        userLocaleApplied.current = true;

        localStorage.setItem(STORAGE_KEY, 'true');

        if (user.locale !== router.locale) {
            void router.push(router.asPath, router.asPath, {locale: user.locale});
        }
    }, [isLoading, router, user]);

    /**
     * Dismisses the popup and marks the language as chosen in localStorage.
     */
    const handleConfirm = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, 'true');
        setDismissed(true);
    }, []);

    return {
        handleConfirm,
        isOpen,
        t
    };
};